import { useCallback, useEffect, useState } from "react";
import type { StudentSkillsResponse } from "@edu/shared";
import { getStudentSkills } from "./student-skills-api.ts";

/**
 * Данные панели «Где дыры»: свой запрос, свой отказ (7.1 проекта).
 *
 * ⚠️ **Отдельно от `useStudent`, хотя карточка собирается одним ответом
 * намеренно.** Причины две, и обе про этот запрос, а не про вкус. Он самый
 * тяжёлый из всех: агрегация по попыткам за 90 суток, кэша у неё нет и быть не
 * должно. И он обязан падать отдельно: панель, уронившая карточку ученика,
 * забрала бы с собой занятия, оплаты и ссылку, то есть всё, ради чего сюда
 * заходят каждый день (7.2, строка «Запрос упал»).
 *
 * До приезда данных панель не занимает места вовсе, поэтому состояния
 * «загружается» здесь нет: `data === null` при `err === null` это оно и есть.
 */
export interface StudentSkillsState {
  data: StudentSkillsResponse | null;
  err: string | null;
  reload: () => void;
}

export function useStudentSkills(studentUserId: string): StudentSkillsState {
  const [data, setData] = useState<StudentSkillsResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    if (studentUserId === "") return;
    setErr(null);
    void (async () => {
      try {
        setData(await getStudentSkills(studentUserId));
      } catch (e) {
        // Прошлый ответ не стираем: перечитывание, упавшее по сети, не обязано
        // уносить с экрана то, что уже прочитано. Строка отказа встанет над ним
        setErr(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [studentUserId]);

  useEffect(load, [load]);

  return { data, err, reload: load };
}
